import '@/assets/stylesheets/stylesheets.scss'
import '@/assets/stylesheets/tailwind.css'

import qs from 'qs'

import {
  createApp,
  registerPlugins,
  registerCommonComponents,
  chooseApiKey,
  detectApplicationType,
  detectWebView,
  getApiUrl,
  getPublicApiUrl,
  getStatsAndReportsUrl,
  getEcommerceUrl,
  detectNativeLocation,
  getBooksyWalletUrl,
  getBooksyWrappedUrl,
  registerServiceWorker,
} from '@/main/functions'

import { createRouter } from '@/router/index.ts'
import { createPinia } from 'pinia'

import { createFingerprint, useLogger } from '@booksy-com/vue-tools'
import { datadogRum } from '@datadog/browser-rum'

import App from '@/app.vue'
import { store } from '@/store'

import { localize } from '@/localize.js'
import { getExternalId } from '@/lib/analytics/helpers.js'

import {
  SUPPORTED_COUNTRIES,
  APP_TYPE,
  WEBVIEW_TYPES,
  EVENTS,
  LOCAL_STORAGE_KEYS,
  DATADOG_CUSTOM_ATTRIBUTES,
  DATADOG_CUSTOM_ACTIONS_NAMES,
  FEATURE_FLAGS,
} from '@/lib/consts.ts'

// composables
import { createCore } from '@/composables/core'
import { useEmitter } from '@/composables/emitter'
import { initSurvicate, useSurvicate } from '@/composables/survicate'
import { initWebTools } from '@/composables/web-tools'
import { createEppo, useEppo, whenEppoIsReady } from '@/composables/eppo'
import { useBooksyFirestore } from '@/firestore/client'
import { useCookies } from 'vue3-cookies'

// BVC
import { bvcOptionsInjectionKey } from '@booksy-com/vue-components-core'
import { getInventoryUrl } from '@/main/functions/get-inventory-url'

registerServiceWorker()

const installBVC = ({ app }) => {
  app.provide(bvcOptionsInjectionKey, {})
}

export async function main () {
  const app = createApp(App)

  const { country, language, locale, i18n, basePath, baseUrl } = await localize()

  app.use(i18n)

  // create & register store
  app.use(store)

  const pinia = createPinia()
  app.use(pinia)

  const router = createRouter()
  app.use(router)

  const logger = useLogger('main')

  // register Vue plugins
  registerPlugins({ app, router })

  // register Vue (common) components
  registerCommonComponents({ app })

  // register BVC components
  installBVC({ app })

  try {
    datadogRum.addAction(DATADOG_CUSTOM_ACTIONS_NAMES.STARTUP_START)
  } catch (e) { /* do nothing */ }

  try {
    datadogRum.setGlobalContextProperty(DATADOG_CUSTOM_ATTRIBUTES.BOOKSY_COUNTRY_CODE, country)
  } catch (e) {
    logger.warn('DD RUM set booksy country code property failed with message: ', e?.message)
  }

  const env = process.env
  const apiUrl = getApiUrl(env, window.location)
  const publicApiUrl = getPublicApiUrl(env)
  const statsAndReportsUrl = getStatsAndReportsUrl(env)
  const ecommerceUrl = getEcommerceUrl(env)
  const booksyWalletUrl = getBooksyWalletUrl(env)
  const booksyWrappedUrl = getBooksyWrappedUrl(env)
  const inventoryUrl = getInventoryUrl(env)

  initWebTools(env)

  const emitter = useEmitter()

  try {
    await createEppo(store)
  } catch { }

  emitter.on(EVENTS.BUSINESS_STATE_CHANGE, () => {
    const user = store.state.identity.user
    const business = store.state.business.business

    if (user && business) {
      whenEppoIsReady.then(() => {
        if (useEppo().ff(FEATURE_FLAGS.Feature_ProviderFirestore)) {
          useBooksyFirestore().init({ user, business })
        }
      })

      datadogRum.setGlobalContextProperty(
        DATADOG_CUSTOM_ATTRIBUTES.SALON_SIZE,
        business.active_staffers_count
      )

      datadogRum.setGlobalContextProperty(
        DATADOG_CUSTOM_ATTRIBUTES.BUSINESS_ID,
        getExternalId({
          id: business.id,
          country,
          routingConfig: store.state.routingConfig.config,
        })
      )

      datadogRum.setGlobalContextProperty(
        DATADOG_CUSTOM_ATTRIBUTES.USER_ID,
        getExternalId({
          id: user.id,
          country,
          routingConfig: store.state.routingConfig.config,
        })
      )

      datadogRum.setUser({
        id: getExternalId({
          id: user.id,
          country,
          routingConfig: store.state.routingConfig.config,
        }),
      })
    } else {
      useBooksyFirestore().destroy()

      datadogRum.removeGlobalContextProperty(DATADOG_CUSTOM_ATTRIBUTES.SALON_SIZE)
      datadogRum.removeGlobalContextProperty(DATADOG_CUSTOM_ATTRIBUTES.BUSINESS_ID)
      datadogRum.removeGlobalContextProperty(DATADOG_CUSTOM_ATTRIBUTES.USER_ID)

      datadogRum.clearUser()
    }

    useSurvicate().updateVisitorIdentity()
  })

  // detect web-view
  detectWebView()

  // detect app type
  detectApplicationType()

  // detect if location should be obtained from native iOS app
  if (localStorage.getItem(LOCAL_STORAGE_KEYS.APP_WEB_VIEW) === WEBVIEW_TYPES.IOS) {
    detectNativeLocation()
  }

  createCore({
    store,
    router,
    i18n,
  })

  initSurvicate()
  useSurvicate().updateVisitorIdentity()

  const queryParams = qs.parse(window.location.search.substring(1))
  const ios_fingerprint = queryParams.ios_fingerprint || null

  logger.debug('setting up store module: app')
  const { cookies } = useCookies()
  const fingerprint = createFingerprint(
    window.location.hostname,
    cookies
  ).get()

  store.dispatch('app/setup', {
    mode: localStorage.getItem(LOCAL_STORAGE_KEYS.APP_MODE) || process.env.NODE_ENV,
    webView: localStorage.getItem(LOCAL_STORAGE_KEYS.APP_WEB_VIEW) || false,
    appType: localStorage.getItem(LOCAL_STORAGE_KEYS.APP_APP_TYPE) || APP_TYPE.PWA,
    supportedCountries: SUPPORTED_COUNTRIES,
    country,
    language,
    locale,
    baseUrl,
    basePath,
    fingerprint,
    ios_fingerprint,
    ecommerceUrl,
    inventoryUrl,
    statsAndReportsUrl,
    booksyWalletUrl,
    booksyWrappedUrl,
  })

  logger.debug('setting up store module: api')
  store.dispatch('api/setup', {
    apiUrl: apiUrl,
    apiKey: chooseApiKey(apiUrl),
    publicApiUrl: publicApiUrl,
  })

  return app
}
